"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { discussionsApi } from "@/lib/api/services/discussions.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Plus, Clock } from "lucide-react";
import Link from "next/link";
import { useRequireAuth } from "@/hooks/use-auth-gate";

interface Discussion {
  id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  author: string;
  createdAt: string;
  replyCount: number;
  upvoteCount: number;
}

export default function CommunityPage() {
  const { requireAuth, AuthModal } = useRequireAuth("/discussions/create");

  const { data: discussions, isLoading } = useQuery({
    queryKey: ["community-discussions"],
    queryFn: async () => {
      const data = await discussionsApi.list();
      return ((data as unknown as { data?: Discussion[] }).data ?? []) as Discussion[];
    },
  });

  const handleNewDiscussion = (e: React.MouseEvent) => {
    if (!requireAuth()) {
      e.preventDefault();
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Community</h1>
          <p className="mt-1 text-sm text-muted-foreground">Discussions, insights, and shared knowledge.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/discussions/create" onClick={handleNewDiscussion}>
            <Plus className="mr-2 h-4 w-4" /> New Discussion
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : !discussions || discussions.length === 0 ? (
            <div className="py-16 text-center">
              <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No discussions yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">Start a conversation with the community.</p>
              <Button className="mt-4" size="sm" asChild>
                <Link href="/discussions/create" onClick={handleNewDiscussion}>Create Discussion</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {discussions.map((discussion) => (
                <Link key={discussion.id} href={`/discussions/${discussion.id}`} className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/10">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                    <MessageSquare className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{discussion.title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{discussion.description}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                    <span className="hidden sm:flex items-center gap-1"><Clock className="h-3 w-3" /> {discussion.createdAt}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {discussion.replyCount}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <AuthModal />
    </div>
  );
}